import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, workAreaAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function DeliveryPartnerManagement() {
  const [partners, setPartners] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showWorkAreaModal, setShowWorkAreaModal] = useState(false);
  const [selectedWorkAreas, setSelectedWorkAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [masterOnline, setMasterOnline] = useState(true);   // master kill-switch
  const [masterSaving, setMasterSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [partnersRes, workAreasRes, masterRes] = await Promise.all([
        adminAPI.getAllDeliveryPartners({ search }),
        workAreaAPI.getAllWorkAreas(),
        adminAPI.getDeliveryPartnersOnline().catch(() => null)
      ]);

      setPartners(partnersRes.data.data.deliveryPartners || partnersRes.data.data.partners || []);
      setWorkAreas(workAreasRes.data.data.workAreas || []);
      if (masterRes) setMasterOnline(masterRes.data?.data?.enabled !== false);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenWorkAreaModal = (partner) => {
    setSelectedPartner(partner);
    // Get current work area IDs
    const currentWorkAreaIds = partner.deliveryPartner?.workAreas?.map(wa =>
      typeof wa === 'object' ? wa._id : wa
    ) || [];
    setSelectedWorkAreas(currentWorkAreaIds);
    setShowWorkAreaModal(true);
  };

  const handleCloseModal = () => {
    setShowWorkAreaModal(false);
    setSelectedPartner(null);
    setSelectedWorkAreas([]);
  };

  const handleToggleWorkArea = (workAreaId) => {
    setSelectedWorkAreas(prev => {
      if (prev.includes(workAreaId)) {
        return prev.filter(id => id !== workAreaId);
      } else {
        return [...prev, workAreaId];
      }
    });
  };

  const handleSelectAllWorkAreas = () => {
    if (selectedWorkAreas.length === workAreas.length) {
      setSelectedWorkAreas([]);
    } else {
      setSelectedWorkAreas(workAreas.map(wa => wa._id));
    }
  };

  const handleSaveWorkAreas = async () => {
    if (!selectedPartner) return;

    try {
      setSaving(true);
      await adminAPI.updateDeliveryPartnerWorkAreas(selectedPartner._id, selectedWorkAreas);
      alert('Work areas updated successfully!');
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Failed to update work areas:', error);
      alert('Failed to update work areas: ' + (error.response?.data?.message || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Master kill-switch: globally allow/block delivery partners going online.
  const handleToggleMaster = async () => {
    const next = !masterOnline;
    if (!next && !window.confirm('Turn OFF delivery globally?\n\nNo delivery partner will be able to go online, and ALL currently-online partners will be forced offline immediately.')) {
      return;
    }
    try {
      setMasterSaving(true);
      const res = await adminAPI.setDeliveryPartnersOnline(next);
      setMasterOnline(next);
      if (!next && res.data?.data?.forcedOffline != null) {
        alert(`Delivery globally disabled. ${res.data.data.forcedOffline} online partner(s) forced offline.`);
      }
      await fetchData(); // refresh — disabling forces partners offline
    } catch (e) {
      alert('Failed to update global delivery switch: ' + (e.response?.data?.message || e.message));
    } finally {
      setMasterSaving(false);
    }
  };

  // Admin override: force a partner online/offline.
  const handleToggleOnline = async (partner) => {
    const next = !partner.deliveryPartner?.isAvailable;
    if (next && !masterOnline) {
      alert('Delivery is globally OFF. Turn on the master switch before setting a partner online.');
      return;
    }
    if (next && partner.isBlocked) {
      alert('Cannot set a blocked partner online — unblock them first.');
      return;
    }
    try {
      setTogglingId(partner._id);
      await adminAPI.updateDeliveryPartnerAvailability(partner._id, next);
      // Optimistic local update so the badge/button flip immediately.
      setPartners(prev => prev.map(p =>
        p._id === partner._id
          ? { ...p, deliveryPartner: { ...(p.deliveryPartner || {}), isAvailable: next } }
          : p
      ));
    } catch (error) {
      alert('Failed to update online status: ' + (error.response?.data?.message || error.message));
    } finally {
      setTogglingId(null);
    }
  };

  // Per-partner online lock: persistently block/allow a specific partner online.
  const handleToggleOnlineAccess = async (partner) => {
    const currentlyBlocked = !!partner.deliveryPartner?.onlineBlocked;
    const allow = currentlyBlocked; // blocked -> allow; allowed -> block
    if (!allow && !window.confirm(`Block ${partner.name} from going online?\n\nThey won't be able to go online and will be forced offline immediately until you allow it again.`)) {
      return;
    }
    try {
      setTogglingId(partner._id);
      await adminAPI.setDeliveryPartnerOnlineAccess(partner._id, allow);
      setPartners(prev => prev.map(p =>
        p._id === partner._id
          ? { ...p, deliveryPartner: { ...(p.deliveryPartner || {}), onlineBlocked: !allow, isAvailable: allow ? p.deliveryPartner?.isAvailable : false } }
          : p
      ));
    } catch (e) {
      alert('Failed to update online access: ' + (e.response?.data?.message || e.message));
    } finally {
      setTogglingId(null);
    }
  };

  // Per-partner: allow/restrict booking gigs across multiple work areas.
  const handleToggleMultiArea = async (partner) => {
    const currentlyAllowed = partner.deliveryPartner?.multiAreaGigBooking !== false;
    const allow = !currentlyAllowed;
    try {
      setTogglingId(partner._id);
      await adminAPI.setDeliveryPartnerMultiAreaGigs(partner._id, allow);
      setPartners(prev => prev.map(p =>
        p._id === partner._id
          ? { ...p, deliveryPartner: { ...(p.deliveryPartner || {}), multiAreaGigBooking: allow } }
          : p
      ));
    } catch (e) {
      alert('Failed to update multi-area gig booking: ' + (e.response?.data?.message || e.message));
    } finally {
      setTogglingId(null);
    }
  };

  // Set a per-partner COD cash-in-hand limit (blank = use global default).
  const handleSetCodLimit = async (partner) => {
    const current = partner.deliveryPartner?.codLimit;
    const input = window.prompt(
      `COD cash limit for ${partner.name} (₹).\nLeave blank to use the global default (₹${partner.codEffectiveLimit ?? 1000}).\nCurrent cash-in-hand: ₹${partner.codCashInHand ?? 0}`,
      current != null ? String(current) : ''
    );
    if (input === null) return; // cancelled
    const trimmed = input.trim();
    const codLimit = trimmed === '' ? null : Number(trimmed);
    if (codLimit !== null && (!Number.isFinite(codLimit) || codLimit < 0)) {
      alert('Please enter a non-negative number, or leave blank for the default.');
      return;
    }
    if (codLimit === 0 && !window.confirm(`Setting the COD limit to ₹0 will BLOCK ${partner.name} from receiving any COD (cash) orders — they'll only get prepaid orders. Continue?`)) {
      return;
    }
    try {
      setTogglingId(partner._id);
      await adminAPI.setDeliveryPartnerCodLimit(partner._id, codLimit);
      setPartners(prev => prev.map(p =>
        p._id === partner._id
          ? { ...p, deliveryPartner: { ...(p.deliveryPartner || {}), codLimit } }
          : p
      ));
    } catch (e) {
      alert('Failed to update COD limit: ' + (e.response?.data?.message || e.message));
    } finally {
      setTogglingId(null);
    }
  };

  const getWorkAreaNames = (partner) => {
    const areas = partner.deliveryPartner?.workAreas || [];
    if (areas.length === 0) return 'No work areas assigned';
    return areas.map(wa => typeof wa === 'object' ? wa.name : 'Unknown').join(', ');
  };

  if (loading) {
    return (
      <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
        <Navbar />
        <div className="container" style={{ textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔄</div>
          <p>Loading delivery partners...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-2xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-4xl)',
            fontWeight: '800',
            background: 'var(--gradient-primary)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            🚴 Delivery Partner Management
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-lg)' }}>
            Manage delivery partners and assign work areas
          </p>
        </div>

        {/* Master kill-switch: globally allow/block delivery partners going online */}
        <div className="card fade-in" style={{
          marginBottom: 'var(--space-xl)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-md)',
          borderLeft: `6px solid ${masterOnline ? 'var(--success)' : 'var(--danger)'}`
        }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 'var(--font-size-lg)' }}>
              🌐 Delivery Partners Online — Master Switch &nbsp;
              <span style={{
                fontSize: 'var(--font-size-sm)', padding: '2px 10px', borderRadius: 'var(--radius-full)',
                background: masterOnline ? 'var(--success)' : 'var(--danger)', color: '#fff'
              }}>{masterOnline ? 'ON' : 'OFF'}</span>
            </div>
            <div style={{ color: 'var(--gray-600)', marginTop: 4 }}>
              {masterOnline
                ? 'Partners can go online normally.'
                : 'OFF — no partner can go online, and all currently-online partners are forced offline.'}
            </div>
          </div>
          <button
            onClick={handleToggleMaster}
            disabled={masterSaving}
            className={masterOnline ? 'btn btn-danger' : 'btn btn-success'}
            style={{ minWidth: '180px', opacity: masterSaving ? 0.6 : 1 }}
          >
            {masterSaving ? '…' : (masterOnline ? '⏻ Turn OFF (block all)' : '⏻ Turn ON (allow online)')}
          </button>
        </div>

        {/* Search Bar */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <div style={{ display: 'flex', gap: 'var(--space-md)', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by name, phone, or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input"
              style={{ flex: 1 }}
            />
            <button onClick={fetchData} className="btn btn-primary">
              🔍 Search
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-lg)', marginBottom: 'var(--space-xl)' }}>
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--primary)' }}>{partners.length}</div>
            <div style={{ color: 'var(--gray-600)' }}>Total Partners</div>
          </div>
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--success)' }}>
              {partners.filter(p => p.deliveryPartner?.isApproved).length}
            </div>
            <div style={{ color: 'var(--gray-600)' }}>Approved</div>
          </div>
          <div className="card fade-in" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--secondary)' }}>{workAreas.length}</div>
            <div style={{ color: 'var(--gray-600)' }}>Work Areas</div>
          </div>
        </div>

        {/* Partners List */}
        {partners.length === 0 ? (
          <div className="card fade-in" style={{ textAlign: 'center', padding: 'var(--space-2xl)' }}>
            <div style={{ fontSize: '80px', marginBottom: 'var(--space-lg)' }}>🚴</div>
            <h2 style={{ color: 'var(--gray-700)' }}>No delivery partners found</h2>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 'var(--space-lg)' }}>
            {partners.map(partner => (
              <div key={partner._id} className="card fade-in" style={{
                position: 'relative',
                borderLeft: `4px solid ${partner.deliveryPartner?.isApproved ? 'var(--success)' : 'var(--warning)'}`
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 'var(--space-md)' }}>
                  {/* Partner Info */}
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3 style={{
                      fontSize: 'var(--font-size-xl)',
                      fontWeight: '700',
                      marginBottom: 'var(--space-xs)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-sm)'
                    }}>
                      🚴 {partner.name}
                      {partner.isBlocked && (
                        <span style={{
                          background: 'var(--danger)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-xs)'
                        }}>BLOCKED</span>
                      )}
                      <span style={{
                        background: partner.deliveryPartner?.isAvailable ? 'var(--success)' : 'var(--gray-400)',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-full)',
                        fontSize: 'var(--font-size-xs)'
                      }}>{partner.deliveryPartner?.isAvailable ? '🟢 ONLINE' : '⚪ OFFLINE'}</span>
                      {partner.deliveryPartner?.onlineBlocked && (
                        <span style={{
                          background: 'var(--warning)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-xs)'
                        }}>🚫 ONLINE BLOCKED</span>
                      )}
                      {partner.deliveryPartner?.multiAreaGigBooking === false && (
                        <span style={{
                          background: 'var(--secondary)',
                          color: 'white',
                          padding: '2px 8px',
                          borderRadius: 'var(--radius-full)',
                          fontSize: 'var(--font-size-xs)'
                        }}>🗺️ SINGLE-AREA</span>
                      )}
                    </h3>
                    <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-xs)' }}>
                      📱 {partner.phone} &nbsp;|&nbsp; 📧 {partner.email || 'N/A'}
                    </p>
                    <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                      🛵 {partner.deliveryPartner?.vehicleType || 'N/A'} &nbsp;|&nbsp;
                      Plate: {partner.deliveryPartner?.vehicleNumber || 'N/A'}
                    </p>
                  </div>

                  {/* Work Areas */}
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <p style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--gray-600)',
                      fontWeight: '600',
                      marginBottom: 'var(--space-xs)'
                    }}>
                      📍 ASSIGNED WORK AREAS
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xs)' }}>
                      {(partner.deliveryPartner?.workAreas || []).length === 0 ? (
                        <span style={{
                          color: 'var(--warning)',
                          fontStyle: 'italic',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          No work areas assigned
                        </span>
                      ) : (
                        partner.deliveryPartner.workAreas.map((wa, idx) => (
                          <span key={idx} style={{
                            background: 'rgba(78, 205, 196, 0.15)',
                            color: 'var(--secondary)',
                            padding: '4px 12px',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-sm)',
                            fontWeight: '600'
                          }}>
                            {typeof wa === 'object' ? `${wa.name} (${wa.city})` : wa}
                          </span>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
                    {(() => {
                      const setOnlineDisabled = togglingId === partner._id ||
                        ((!masterOnline || partner.deliveryPartner?.onlineBlocked) && !partner.deliveryPartner?.isAvailable);
                      const setOnlineTitle = partner.deliveryPartner?.onlineBlocked && !partner.deliveryPartner?.isAvailable
                        ? "This partner's online access is blocked — allow it first"
                        : (!masterOnline && !partner.deliveryPartner?.isAvailable
                          ? 'Master switch is OFF — enable it to set partners online'
                          : (partner.deliveryPartner?.isAvailable ? 'Force this partner offline' : 'Force this partner online'));
                      return (
                        <button
                          onClick={() => handleToggleOnline(partner)}
                          disabled={setOnlineDisabled}
                          className={partner.deliveryPartner?.isAvailable ? 'btn btn-danger' : 'btn btn-success'}
                          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', opacity: setOnlineDisabled ? 0.6 : 1 }}
                          title={setOnlineTitle}
                        >
                          {togglingId === partner._id ? '…' : (partner.deliveryPartner?.isAvailable ? '⏸ Set Offline' : '▶ Set Online')}
                        </button>
                      );
                    })()}
                    <button
                      onClick={() => handleToggleOnlineAccess(partner)}
                      disabled={togglingId === partner._id}
                      className={partner.deliveryPartner?.onlineBlocked ? 'btn btn-success' : 'btn btn-secondary'}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', opacity: togglingId === partner._id ? 0.6 : 1 }}
                      title={partner.deliveryPartner?.onlineBlocked ? 'Allow this partner to go online again' : 'Block this partner from going online (persistent)'}
                    >
                      {partner.deliveryPartner?.onlineBlocked ? '✅ Allow Online' : '🚫 Block Online'}
                    </button>
                    <button
                      onClick={() => handleToggleMultiArea(partner)}
                      disabled={togglingId === partner._id}
                      className={partner.deliveryPartner?.multiAreaGigBooking === false ? 'btn btn-success' : 'btn btn-secondary'}
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', opacity: togglingId === partner._id ? 0.6 : 1 }}
                      title={partner.deliveryPartner?.multiAreaGigBooking === false ? 'Allow this partner to book gigs in multiple work areas' : 'Restrict this partner to booking gigs in a single work area'}
                    >
                      {partner.deliveryPartner?.multiAreaGigBooking === false ? '🗺️ Allow Multi-area' : '🗺️ Restrict to 1 area'}
                    </button>
                    <button
                      onClick={() => handleSetCodLimit(partner)}
                      disabled={togglingId === partner._id}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', opacity: togglingId === partner._id ? 0.6 : 1 }}
                      title="Set this partner's COD cash-in-hand limit (₹). Blank = global default."
                    >
                      💵 COD Limit: {partner.deliveryPartner?.codLimit != null ? `₹${partner.deliveryPartner.codLimit}` : `Default (₹${partner.codEffectiveLimit ?? 1000})`}
                    </button>
                    <button
                      onClick={() => handleOpenWorkAreaModal(partner)}
                      className="btn btn-secondary"
                      style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}
                    >
                      📍 Manage Work Areas
                    </button>
                  </div>
                  {/* Live COD cash-in-hand vs limit */}
                  <div style={{ marginTop: '8px', fontSize: '13px', color: (partner.codCashInHand ?? 0) >= (partner.codEffectiveLimit ?? 1000) ? '#b45309' : '#6b7280' }}>
                    💰 Cash-in-hand: <strong>₹{partner.codCashInHand ?? 0}</strong> / ₹{partner.codEffectiveLimit ?? 1000}
                    {partner.codPendingCount > 0 ? ` (${partner.codPendingCount} unremitted COD order${partner.codPendingCount > 1 ? 's' : ''})` : ''}
                    {(partner.codCashInHand ?? 0) >= (partner.codEffectiveLimit ?? 1000) ? ' — at limit, COD blocked until deposit' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Work Area Assignment Modal */}
        {showWorkAreaModal && selectedPartner && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 'var(--space-lg)'
          }} onClick={handleCloseModal}>
            <div
              className="card"
              style={{
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflow: 'auto'
              }}
              onClick={e => e.stopPropagation()}
            >
              <h2 style={{
                fontSize: 'var(--font-size-2xl)',
                fontWeight: '700',
                marginBottom: 'var(--space-lg)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)'
              }}>
                📍 Assign Work Areas
              </h2>

              <p style={{ color: 'var(--gray-600)', marginBottom: 'var(--space-lg)' }}>
                Partner: <strong>{selectedPartner.name}</strong> ({selectedPartner.phone})
              </p>

              {/* Select All */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-md)',
                padding: 'var(--space-sm)',
                background: 'var(--gray-50)',
                borderRadius: 'var(--radius-md)'
              }}>
                <span style={{ fontWeight: '600' }}>
                  {selectedWorkAreas.length} of {workAreas.length} selected
                </span>
                <button
                  onClick={handleSelectAllWorkAreas}
                  className="btn btn-sm"
                  style={{ background: 'var(--gray-200)' }}
                >
                  {selectedWorkAreas.length === workAreas.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>

              {/* Work Area Checkboxes */}
              <div style={{
                display: 'grid',
                gap: 'var(--space-sm)',
                maxHeight: '300px',
                overflowY: 'auto',
                padding: 'var(--space-sm)',
                border: '1px solid var(--gray-200)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--space-xl)'
              }}>
                {workAreas.map(area => (
                  <label
                    key={area._id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      padding: 'var(--space-md)',
                      background: selectedWorkAreas.includes(area._id) ? 'rgba(78, 205, 196, 0.1)' : 'white',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      border: selectedWorkAreas.includes(area._id) ? '2px solid var(--secondary)' : '1px solid var(--gray-200)',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedWorkAreas.includes(area._id)}
                      onChange={() => handleToggleWorkArea(area._id)}
                      style={{
                        width: '20px',
                        height: '20px',
                        accentColor: 'var(--secondary)'
                      }}
                    />
                    <div>
                      <span style={{ fontWeight: '600' }}>{area.name}</span>
                      <span style={{
                        color: 'var(--gray-500)',
                        marginLeft: 'var(--space-sm)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        ({area.city})
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: 'var(--space-md)', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleCloseModal}
                  className="btn"
                  style={{ background: 'var(--gray-200)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveWorkAreas}
                  className="btn btn-success"
                  disabled={saving}
                >
                  {saving ? '💾 Saving...' : '💾 Save Work Areas'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DeliveryPartnerManagement;
